import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
    Box,
    IconButton,
    Typography,
    Paper,
    Grid,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Container,
    Card,
    CardContent,
    Divider,
    useTheme,
    alpha,
    Stack
} from "@mui/material";
import { designTokens } from '../theme/designTokens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { faqs } from '../metadata/faqs';

const Faq = () => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const faqIndex = faqs.findIndex(f => f.id === id);
            if (faqIndex !== -1) {
                setExpanded(`panel${faqIndex + 1}`);
                setTimeout(() => {
                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location.hash]);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: designTokens.spacing['3xl'] / 8 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                        p: designTokens.spacing['6xl'] / 8,
                        mb: designTokens.spacing['6xl'] / 8,
                        textAlign: 'center',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                >
                    <Stack spacing={designTokens.spacing['2xl'] / 8} alignItems="center">
                        <Box
                            sx={{
                                width: designTokens.spacing['7xl'],
                                height: designTokens.spacing['7xl'],
                                borderRadius: designTokens.borderRadius.full,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: designTokens.shadows.xl,
                            }}
                        >
                            <HelpOutlineIcon sx={{ fontSize: designTokens.spacing['5xl'], color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{
                                    fontWeight: designTokens.fontWeight.bold,
                                    color: 'text.primary',
                                    mb: designTokens.spacing.lg / 8,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Frequently Asked Questions
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: designTokens.fontWeight.normal,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    lineHeight: designTokens.lineHeight.relaxed,
                                }}
                            >
                                Find answers to common questions about RWE DataLab and get the support you need
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* FAQ Section */}
                <Box sx={{ mb: designTokens.spacing['6xl'] / 8 }}>
                    <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                            mb: designTokens.spacing['3xl'] / 8,
                            fontWeight: designTokens.fontWeight.semibold,
                            color: 'text.primary',
                        }}
                    >
                        Common Questions
                    </Typography>

                    <Stack spacing={designTokens.spacing.lg / 8}>
                        {faqs.map((faq, index) => (
                            <Accordion
                                key={index}
                                id={faq.id || undefined}
                                expanded={expanded === `panel${index + 1}`}
                                onChange={handleChange(`panel${index + 1}`)}
                                sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    boxShadow: expanded === `panel${index + 1}`
                                        ? designTokens.shadows.xl
                                        : designTokens.shadows.sm,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: designTokens.shadows.md,
                                    },
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                color: expanded === `panel${index + 1}`
                                                    ? 'primary.main'
                                                    : 'text.secondary',
                                                transition: 'color 0.2s ease-in-out',
                                            }}
                                        />
                                    }
                                    aria-controls={`panel${index + 1}bh-content`}
                                    id={`panel${index + 1}bh-header`}
                                    sx={{
                                        backgroundColor: expanded === `panel${index + 1}`
                                            ? alpha(theme.palette.primary.main, 0.04)
                                            : 'transparent',
                                        minHeight: designTokens.spacing['7xl'],
                                        px: designTokens.spacing['2xl'] / 8,
                                        '&.Mui-expanded': {
                                            borderBottomLeftRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                        transition: 'background-color 0.2s ease-in-out',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: designTokens.fontWeight.medium,
                                            color: expanded === `panel${index + 1}`
                                                ? 'primary.main'
                                                : 'text.primary',
                                            transition: 'color 0.2s ease-in-out',
                                            pr: designTokens.spacing.lg / 8,
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: designTokens.spacing['2xl'] / 8,
                                        py: designTokens.spacing['2xl'] / 8,
                                        backgroundColor: 'background.paper',
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.primary',
                                            lineHeight: designTokens.lineHeight.relaxed,
                                            fontSize: designTokens.fontSize.base,
                                            whiteSpace: 'pre-line',
                                        }}
                                    >
                                        {typeof faq.answer === 'object' ? (
                                            <>
                                                {faq.answer.text}{' '}
                                                <Link
                                                    to={faq.answer.link.href}
                                                    style={{ color: theme.palette.primary.main, fontWeight: 500 }}
                                                >
                                                    {faq.answer.link.label}
                                                </Link>
                                            </>
                                        ) : (
                                            faq.answer
                                        )}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>

                {/* Contact Support Section */}
                <Paper
                    elevation={0}
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            px: designTokens.spacing['3xl'] / 8,
                            py: designTokens.spacing['2xl'] / 8,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" spacing={designTokens.spacing.lg / 8} alignItems="center">
                            <SupportAgentIcon sx={{ fontSize: designTokens.spacing['3xl'], color: 'secondary.main' }} />
                            <Box>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    sx={{
                                        fontWeight: designTokens.fontWeight.semibold,
                                        color: 'text.primary',
                                        mb: designTokens.spacing.xs / 8,
                                    }}
                                >
                                    Need Additional Support?
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    {`Can't find what you're looking for? Our support team is here to help.`}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ p: designTokens.spacing['3xl'] / 8 }}>
                        <Grid container spacing={designTokens.spacing['3xl'] / 8}>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: designTokens.spacing['2xl'] / 8,
                                        height: '100%',
                                        border: `1px solid ${theme.palette.divider}`,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: designTokens.shadows.md,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                width: designTokens.spacing['7xl'],
                                                height: designTokens.spacing['7xl'],
                                                borderRadius: designTokens.borderRadius.full,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: `0 auto ${designTokens.spacing.lg}px auto`,
                                            }}
                                        >
                                            <PhoneIcon sx={{ fontSize: designTokens.spacing['3xl'], color: 'primary.main' }} />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: designTokens.fontWeight.semibold,
                                                mb: designTokens.spacing.sm / 8,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Phone Support
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'primary.main',
                                                fontWeight: designTokens.fontWeight.medium,
                                                mb: designTokens.spacing.sm / 8,
                                            }}
                                        >
                                            (317) 207-2136
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Monday - Friday, 8:00 AM - 5:00 PM EST
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        height: '100%',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 16px auto',
                                            }}
                                        >
                                            <EmailIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Email Support
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'secondary.main',
                                                fontWeight: 500,
                                                mb: 1,
                                            }}
                                        >
                                            support@rwedatalab.com
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            We typically respond within 24 hours
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default Faq;